import { Field, Item, LinkField, NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { useMemo, useRef } from 'react';
import { useI18n } from 'next-localization';
import useLanguageVersions from 'hooks/useLanguageVersions';
import { usePageData } from 'lib/page-data/PageDataContext';

import GlobalNavigationDropdown from 'components/Generic/Header/GlobalNavigation/GlobalNavigationDropdown';
import SiteSelector from 'components/Generic/Header/GlobalNavigation/SiteSelector';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import { LanguageVersion } from 'src/model/LanguageVersion';
import { useChangeLocale } from 'hooks/useChangeLocale';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

export type GlobalNavigationFields = Item & {
  fields: {
    ctaSecondary: LinkField;
    cta: LinkField;
    icon: Field<string>;
    iconSecondary: Field<string>;
  };
};

type GlobalNavigationProps = {
  globalNavFields: GlobalNavigationFields;
};

const GlobalNavigation = (props: GlobalNavigationProps) => {
  const languageSelector = useRef(null);
  const changeLocale = useChangeLocale();
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext && sitecoreContext.pageEditing;
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const icon = require('../../../../assets/img/custom-svg/artwork.svg').default;

  const languageVersions = useLanguageVersions();

  const selectLanguage = (item: LanguageVersion) => {
    changeLocale(item.hreflang);
  };

  const sharePriceCta = sitecoreContext?.sharePriceSettings?.sharePriceCta;
  const sharePriceValue = usePageData('sharePrice') || null;

  const secondaryCtaExist =
    (props.globalNavFields.fields?.ctaSecondary?.value?.href &&
      props.globalNavFields.fields?.ctaSecondary?.value?.text) ||
    isExperienceEditor;

  const getSecondaryIcon = props.globalNavFields?.fields?.iconSecondary?.value;
  const getPrimaryIcon = props.globalNavFields?.fields?.icon?.value;
  const ctaExist =
    (props.globalNavFields.fields?.cta?.value?.href &&
      props.globalNavFields.fields?.cta?.value?.text) ||
    isExperienceEditor;

  const selectedOption = useMemo(() => {
    const lang = sitecoreContext.language;

    return languageVersions.find((item) => {
      return item?.hreflang === lang;
    });
  }, [languageVersions, sitecoreContext.language]);

  const hasLanguageSelector = useMemo(() => {
    return languageVersions && selectedOption && languageVersions.length > 1;
  }, [languageVersions, selectedOption]);

  const Component = () => {
    return (
      languageVersions &&
      languageVersions.length && (
        <div className="lang-selector-dropdown">
          {languageVersions.map((lang) => (
            <button
              key={lang.label}
              type="button"
              className={`btn btn-naked lang-selector-item p-2 form-label w-100 ${
                lang.hreflang === selectedOption?.hreflang && 'selected'
              }`}
              onClick={() => selectLanguage(lang)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  selectLanguage(lang);
                }
              }}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )
    );
  };

  return (
    <div className="global-navigation-root">
      <div className="container d-flex justify-content-end">
        <SiteSelector className={`${hasLanguageSelector ? 'me-4' : 'me-auto'}`} />
        {hasLanguageSelector && (
          <GlobalNavigationDropdown
            className="dropdown me-auto d-none d-xl-flex"
            top={24}
            componentToPassDown={<Component />}
          >
            <div
              ref={languageSelector}
              className="background-white h-100 px-3 d-flex align-items-center body-small"
            >
              <NextImage field={icon} alt={t('changeLanguage')} width="16" height="16" />
              <p className="ms-2">{selectedOption?.languageName}</p>
              <i className="bi bi-chevron-down chevron-icon ms-1" style={{ fontSize: '10px' }} />
            </div>
          </GlobalNavigationDropdown>
        )}
        {sharePriceCta && sharePriceValue && (
          <ScLink
            field={sharePriceCta}
            replaceWithSlot={true}
            showExternalIcon={false}
            className="sitecore-link text-white body-small d-none d-md-flex align-items-center me-3 router-link-exact-active router-link-active internal-link"
          >
            <span className="text-decoration-underline">{t('sharePrice')}:</span>
            <span className="share-price-value ms-1">
              {sharePriceValue} {t('sharePriceCurrency')}
            </span>
          </ScLink>
        )}
        {secondaryCtaExist && (
          <ScLink
            field={props.globalNavFields.fields.ctaSecondary}
            replaceWithSlot={false}
            showExternalIcon={false}
            className="body-default navigation-link secondary-link py-0 px-2 mx-2 d-md-flex align-items-center"
          >
            {getSecondaryIcon && (
              <em className={`text-black login-section-icon ms-1 bi-${getSecondaryIcon}`} />
            )}
          </ScLink>
        )}
        {ctaExist && (
          <ScLink
            field={props.globalNavFields.fields.cta}
            replaceWithSlot={false}
            showExternalIcon={false}
            className="body-default navigation-link py-0 px-2 d-none d-md-flex align-items-center"
          >
            {getPrimaryIcon && (
              <em className={`text-black login-section-icon ms-1 bi-${getPrimaryIcon}`} />
            )}
          </ScLink>
        )}
      </div>
    </div>
  );
};
export default GlobalNavigation;
